import { Injectable } from '@angular/core';
import { PlatformExtensionFieldIds, SidepanelService } from '@galaxy/crm/static';
import { CRMCompanyApiService, GetMultiCrmObjectRequest } from '@vendasta/crm';
import { BehaviorSubject, filter, map, Observable, of, switchMap, take } from 'rxjs';
import { CurrentSnapshot, SnapshotService } from '../../../core/snapshot.service';
import { CompanyAccountService } from '../../company-account.service';
import { CreateSnapshotParams, SnapshotSidepanelComponent } from './snapshot-sidepanel.component';

export interface SnapshotSidepanelInputs {
  serviceInputs: SnapshotSidepanelServiceInputs;
  componentInputs: SnapshotSidepanelComponentInputs;
}

export interface SnapshotSidepanelServiceInputs {
  accountGroupID: string;
}

export interface SnapshotSidepanelComponentInputs {
  companyID: string;
  partnerID: string;
  snapshotName: string;
  trackingCategory: string;
}

@Injectable()
export class SnapshotSidepanelService {
  private readonly accountGroupId$$ = new BehaviorSubject<string>(null);
  accountGroupId$ = this.accountGroupId$$.asObservable();
  private readonly snapshotId$$ = new BehaviorSubject<string>(null);
  snapshotId$ = this.snapshotId$$.asObservable();

  constructor(
    private sidepanelService: SidepanelService,
    private readonly snapshotService: SnapshotService,
    private readonly companyService: CRMCompanyApiService,
    private readonly accountCreationService: CompanyAccountService,
  ) {}

  openSidepanel(snapshotSidepanelInputs: SnapshotSidepanelInputs): void {
    const ref = this.sidepanelService.openSidepanel(SnapshotSidepanelComponent);
    ref?.instance?.setInputs(snapshotSidepanelInputs.componentInputs);
    this.accountGroupId$$.next(snapshotSidepanelInputs.serviceInputs.accountGroupID);
    this.sidepanelService.setHeaderTitle(snapshotSidepanelInputs.componentInputs.snapshotName);
  }

  closeSidepanel(): void {
    this.sidepanelService.closeSidepanel();
  }

  createSnapshot(req: CreateSnapshotParams): Observable<string> {
    const upToDateAccountGroupId$ = req.accountGroupId
      ? of(req.accountGroupId)
      : this.fetchAccountGroupId$(req.companyId, req.partnerId);

    const work$ = upToDateAccountGroupId$.pipe(
      take(1),
      switchMap((accountGroupId) => {
        req.accountGroupId = accountGroupId;

        const newSnapshot$ = this.snapshotService.createCompanySnapshot(req);
        return newSnapshot$;
      }),
    );
    return work$;
  }

  private fetchAccountGroupId$(companyId: string, partnerId: string): Observable<string> {
    const createAccountGroup$ = this.accountCreationService.doCreateAccountGroup(companyId, partnerId).pipe(
      map((res) => {
        const accountGroupId = res?.accountGroupId;
        if (accountGroupId) {
          this.accountGroupId$$.next(accountGroupId);
        }
        return accountGroupId;
      }),
    );
    return this.accountGroupId$$.pipe(
      take(1),
      switchMap((accountGroupId) => {
        return accountGroupId ? of(accountGroupId) : createAccountGroup$;
      }),
    );
  }

  getCurrentSnapshot$(partnerId: string, companyId: string): Observable<CurrentSnapshot> {
    return this.companyService
      .getMultiCompany({
        namespace: partnerId,
        crmObjectIds: [companyId],
      } as GetMultiCrmObjectRequest)
      .pipe(
        take(1),
        switchMap((res) => {
          const currentAGID = res?.crmObjects?.[0].fields.find(
            (field) => field.fieldId === PlatformExtensionFieldIds.AccountGroupID,
          );

          if (!currentAGID) {
            return of(null);
          }
          this.accountGroupId$$.next(currentAGID.stringValue);
          this.snapshotService.getCurrentSnapshotV2$(currentAGID.stringValue);
          return this.snapshotService.currentSnapshotLoading$(currentAGID.stringValue).pipe(
            filter((loading) => !loading),
            switchMap(() => {
              return this.snapshotService.getCurrentSnapshotSuccess$(currentAGID.stringValue);
            }),
            switchMap((success) => {
              return success ? this.snapshotService.getCurrentSnapshotUpdates$(currentAGID.stringValue) : of(null);
            }),
          );
        }),
      );
  }
}
